import React, {
	FC,
	ReactNode,
	CSSProperties,
	useContext,
	useState,
	useRef,
	useCallback,
	useLayoutEffect,
	useEffect,
} from 'react';
import {createPortal} from 'react-dom';
import {OverlayContainerContext} from '@/common/contexts/OverlayContainerContext';
import styles from './Tooltip.css';

type DivNativeProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export interface TooltipProps extends Omit<DivNativeProps, 'title'> {
	title: ReactNode;
	positionX: 'left' | 'middle' | 'right';
	zIndex?: number;
}

export const Tooltip: FC<TooltipProps> = props => {
	const {title, children, positionX, zIndex, ...nativeProps} = props;

	const titleContainer = useContext(OverlayContainerContext)!;

	const titleRef = useRef<HTMLParagraphElement>(null);

	const showTimerRef = useRef<number>(0);

	const [titleShownWithStyles, setTitleShownWithStyles] = useState<CSSProperties | null>(null);

	// Show in the defined position
	const handleShow = useCallback(
		(target: HTMLElement) => {
			const targetRect = target.getBoundingClientRect();

			if (positionX === 'left') {
				setTitleShownWithStyles({
					top: targetRect.bottom,
					right: window.innerWidth - targetRect.right,
				});
			} else if (positionX === 'middle') {
				setTitleShownWithStyles({
					top: targetRect.bottom,
					left: targetRect.left + targetRect.width / 2,
					transform: `translateX(-50%)`,
				});
			} else if (positionX === 'right') {
				setTitleShownWithStyles({
					top: targetRect.bottom,
					left: targetRect.left,
				});
			}
		},
		[positionX],
	);

	// Initialization a deferred show, which will happen if the pointer does not leave tht target element
	const handleShowStart = useCallback(
		(event: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
			const pendingTime = 300;

			if (showTimerRef.current === 0) {
				const target = event.currentTarget;
				showTimerRef.current = window.setTimeout(() => {
					handleShow(target);
					showTimerRef.current = 0;
				}, pendingTime);
			}
		},
		[handleShow],
	);

	// Break deferred showing on a pinter leave
	const handleShowStop = useCallback(() => {
		clearTimeout(showTimerRef.current);
		showTimerRef.current = 0;
	}, []);

	// Hide the shown title
	const handleHide = useCallback(() => {
		setTitleShownWithStyles(null);
	}, []);

	// After the title showing check if is fits into the viewport, if not - shift it.
	// Ignore in SSR mode
	if (typeof window !== 'undefined') {
		useLayoutEffect(() => {
			if (!titleShownWithStyles) {
				return;
			}

			const {left: leftPadding, right: rightOffset} = titleRef.current!.getBoundingClientRect();
			const rightPadding = window.innerWidth - rightOffset;
			const minPadding = 8;

			// Foolproof to avoid stack overflow error
			if ('marginLeft' in titleShownWithStyles) {
				return;
			}

			if (leftPadding < minPadding) {
				setTitleShownWithStyles({
					...titleShownWithStyles,
					marginLeft: -leftPadding + minPadding + 1,
				});
			}

			if (rightPadding < minPadding) {
				setTitleShownWithStyles({
					...titleShownWithStyles,
					marginLeft: rightPadding - minPadding - 1,
				});
			}
		}, [titleShownWithStyles]);
	}

	useEffect(() => {
		if (!titleShownWithStyles) {
			return;
		}

		const handler = handleHide;
		window.addEventListener('scroll', handler);
		return () => {
			window.removeEventListener('scroll', handler);
		};
	}, [titleShownWithStyles]);

	return (
		<div
			{...nativeProps}
			onMouseEnter={handleShowStart}
			onMouseLeave={titleShownWithStyles ? handleHide : handleShowStop}
			onTouchStart={titleShownWithStyles ? handleHide : handleShowStart}>
			{children}
			{titleShownWithStyles &&
				createPortal(
					<p ref={titleRef} className={styles.title} style={{...titleShownWithStyles, zIndex}}>
						{title}
					</p>,
					titleContainer,
				)}
		</div>
	);
};

Tooltip.displayName = 'Tooltip';
